import { useState, useRef, FormEvent } from 'react';

interface CTASectionProps {
  sectionRef: React.RefObject<HTMLElement | null>;
}

const CTASection: React.FC<CTASectionProps> = ({ sectionRef }) => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);
  const formRef = useRef<HTMLFormElement>(null);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const textarea = form.querySelector('textarea[name="message"]') as HTMLTextAreaElement;
    if (textarea && textarea.value.length > 500) {
      alert('メッセージは500文字以内でご入力ください。');
      return;
    }
    setLoading(true);
    const data = new URLSearchParams(new FormData(form) as unknown as URLSearchParams);
    try {
      await fetch('https://readdy.ai/api/form/d6to5s9pcqg5q0qh6igg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      setSubmitted(true);
    } catch {
      // silently handle
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Closing banner */}
      <section className="py-20 bg-slate-800 text-center px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-sky-400 text-sm font-bold tracking-widest uppercase mb-4">クロージング</p>
          <h2 className="text-3xl md:text-4xl font-bold text-white leading-tight mb-5">
            被害が出てからでは<br />遅い時代です
          </h2>
          <p className="text-slate-300 text-base leading-relaxed mb-8 max-w-xl mx-auto">
            「うちは大丈夫」と思っていたその企業が被害を受けています。<br />
            地域密着のパートナーとして、神奈川県の中小企業をしっかりサポートします。
          </p>
          <button
            onClick={() => {
              const el = document.getElementById('cta-form');
              el?.scrollIntoView({ behavior: 'smooth' });
            }}
            className="whitespace-nowrap inline-block bg-sky-500 hover:bg-sky-400 text-white font-bold px-10 py-4 rounded-full text-base transition-all duration-200 cursor-pointer"
          >
            まずは無料診断を申し込む
          </button>
        </div>
      </section>

      {/* CTA Form */}
      <section
        id="cta-form"
        ref={sectionRef as React.RefObject<HTMLElement>}
        className="py-24 bg-sky-50"
      >
        <div className="max-w-3xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block text-sky-600 text-sm font-bold tracking-widest uppercase mb-4">
              お問い合わせ
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 leading-tight">
              まずは無料診断から<br />ご相談ください
            </h2>
            <p className="mt-4 text-slate-500 text-base max-w-xl mx-auto leading-relaxed">
              セキュリティ簡易診断・AIリスク診断・現状ヒアリングを無料で行っています。
              お気軽にご連絡ください。
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-4">
              {['セキュリティ簡易診断 無料', 'AIリスク診断 無料', 'ネットワーク診断 無料'].map((tag) => (
                <span key={tag} className="bg-sky-100 text-sky-700 text-xs font-semibold px-4 py-2 rounded-full">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {submitted ? (
            <div className="bg-white rounded-2xl p-12 text-center border border-sky-100">
              <h3 className="text-xl font-bold text-slate-800 mb-3">お問い合わせを受け付けました</h3>
              <p className="text-slate-500 text-sm leading-relaxed">
                ご連絡いただきありがとうございます。<br />
                担当者より折り返しご連絡いたします。<br />
                しばらくお待ちください。
              </p>
            </div>
          ) : (
            <form
              ref={formRef}
              data-readdy-form
              onSubmit={handleSubmit}
              className="bg-white rounded-2xl p-8 md:p-10 border border-sky-100 flex flex-col gap-6"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="flex flex-col gap-1.5">
                  <label className="text-slate-700 text-xs font-semibold">
                    会社名 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="company"
                    required
                    placeholder="株式会社〇〇"
                    className="border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-sky-400 transition-colors placeholder:text-slate-300"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-slate-700 text-xs font-semibold">
                    お名前 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    placeholder="山田 太郎"
                    className="border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-sky-400 transition-colors placeholder:text-slate-300"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="flex flex-col gap-1.5">
                  <label className="text-slate-700 text-xs font-semibold">
                    電話番号 <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    required
                    placeholder="046-XXX-XXXX"
                    className="border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-sky-400 transition-colors placeholder:text-slate-300"
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className="text-slate-700 text-xs font-semibold">メールアドレス</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="info@example.com"
                    className="border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-sky-400 transition-colors placeholder:text-slate-300"
                  />
                </div>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-slate-700 text-xs font-semibold">従業員数</label>
                <select
                  name="employees"
                  className="border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-sky-400 transition-colors bg-white cursor-pointer"
                >
                  <option value="">選択してください</option>
                  <option value="1-5名">1〜5名</option>
                  <option value="6-20名">6〜20名</option>
                  <option value="21-50名">21〜50名</option>
                  <option value="51名以上">51名以上</option>
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-slate-700 text-xs font-semibold">お問い合わせ内容</label>
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3 mb-1">
                  <p className="text-emerald-800 text-xs font-bold">PC・機器の引き取りは完全無料です</p>
                  <p className="text-emerald-600 text-xs mt-0.5">出張費・データ消去費 すべて0円。下の選択肢からお選びください。</p>
                </div>
                <select
                  name="inquiry_type"
                  className="border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-sky-400 transition-colors bg-white cursor-pointer"
                >
                  <option value="">選択してください</option>
                  <option value="不要PC引き取り">不要なPC・機器の引き取りを依頼したい（無料）</option>
                  <option value="無料セキュリティ診断">無料セキュリティ診断を受けたい</option>
                  <option value="UTM導入相談">UTM導入について相談したい</option>
                  <option value="AIリスク診断">AIリスク診断を受けたい</option>
                  <option value="ネットワーク診断">オフィスのネットワーク診断をしてほしい</option>
                  <option value="その他">その他</option>
                </select>
              </div>

              <div className="flex flex-col gap-1.5">
                <label className="text-slate-700 text-xs font-semibold">
                  ご要望・ご質問（任意）
                  <span className="ml-2 text-slate-400 font-normal">{charCount}/500文字</span>
                </label>
                <textarea
                  name="message"
                  rows={4}
                  maxLength={500}
                  placeholder="現状の課題や気になること、ご質問などをご自由にお書きください。"
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className="border border-slate-200 rounded-lg px-4 py-3 text-sm text-slate-700 focus:outline-none focus:border-sky-400 transition-colors resize-none placeholder:text-slate-300"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="whitespace-nowrap w-full bg-sky-600 hover:bg-sky-500 disabled:bg-slate-300 text-white font-bold py-4 rounded-xl text-base transition-all duration-200 cursor-pointer"
              >
                {loading ? '送信中...' : '無料診断を申し込む（完全無料）'}
              </button>

              <p className="text-xs text-slate-400 text-center leading-relaxed">
                ご入力いただいた個人情報は、お問い合わせへの対応のみに使用します。<br />
                第三者への提供は一切行いません。
              </p>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-sky-900 py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start mb-8">
            {/* Company info */}
            <div>
              <p className="text-white text-base font-bold mb-1">株式会社湘南ビジネスサポート</p>
              <p className="text-sky-300 text-xs mb-5">代表取締役　森　尚樹</p>
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-3">
                  <span className="text-sky-200 text-sm">神奈川県大和市南林間2丁目18-4</span>
                </div>
                <div className="flex items-center gap-3">
                  <a href="tel:09027740856" className="text-sky-200 text-sm hover:text-white transition-colors cursor-pointer">
                    090-2774-0856
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <a href="mailto:info.shonanbiz.spt@gmail.com" className="text-sky-200 text-sm hover:text-white transition-colors cursor-pointer break-all">
                    info.shonanbiz.spt@gmail.com
                  </a>
                </div>
              </div>
            </div>
            {/* Service area */}
            <div>
              <p className="text-sky-400 text-xs font-bold tracking-widest uppercase mb-4">対応エリア</p>
              <div className="flex flex-wrap gap-2">
                {['神奈川県全域'].map((area) => (
                  <span key={area} className="bg-sky-800 text-sky-200 text-xs px-3 py-1.5 rounded-full">
                    {area}
                  </span>
                ))}
              </div>
              <p className="text-sky-300 text-xs mt-5 leading-relaxed">
                セキュリティ対策・ネットワーク構築・PC引き取りなど、<br />
                ITに関することはお気軽にご相談ください。
              </p>
            </div>
          </div>

          <div className="pt-6 border-t border-sky-800 flex flex-col md:flex-row items-center justify-between gap-3">
            <p className="text-sky-400 text-xs">
              &copy; {new Date().getFullYear()} 株式会社湘南ビジネスサポート All Rights Reserved.
            </p>
            <p className="text-sky-500 text-xs">神奈川県大和市を拠点に中小企業のITをサポート</p>
          </div>
        </div>
      </footer>
    </>
  );
};

export default CTASection;
